import { useState, useRef, useEffect, useCallback, type FormEvent, type MouseEvent } from 'react';
import { MotifButton, MotifIcon } from '@ey-xd/motif-wc-react';
import { IconoirXmark } from '@ey-xd/motif-icon/Icons/Iconoir/Xmark.js';
import { IconoirClockRotateRight } from '@ey-xd/motif-icon/Icons/Iconoir/ClockRotateRight.js';
import { IconoirSendDiagonal } from '@ey-xd/motif-icon/Icons/Iconoir/SendDiagonal.js';
import { IconoirThumbsUp } from '@ey-xd/motif-icon/Icons/Iconoir/ThumbsUp.js';
import { IconoirThumbsDown } from '@ey-xd/motif-icon/Icons/Iconoir/ThumbsDown.js';
import { IconoirCopy } from '@ey-xd/motif-icon/Icons/Iconoir/Copy.js';
import { IconoirCheck } from '@ey-xd/motif-icon/Icons/Iconoir/Check.js';
import { http } from '@/http/http';
import { Endpoints } from '@/http/endpoints';
import { useClientStore } from '@/client/client-store';
import { authModule } from '@/auth/auth-module';
import { getCurrentUserType } from '@/auth/user-type';
import { useUserAvatar } from '@/header/user-avatar';
import { useChatbotStore } from '@/header/chatbot-store';
import { navigateBrowserToLegacyTarget } from '@/router/legacy-route-adapter';
import { getTracker } from '@/telemetry/tracker';
import { envNumber } from '@/config/env-flags';

// ── Types ──────────────────────────────────────────────────────────────────────

interface ChatResponse {
  response: string;
  questionId: number;
  followQuestions?: string[];
  attachments?: string;
  data?: {
    incomeSummary?: { items?: { scheduleLine: string; taxTotal: number }[] };
    addNewPrintJob?: {
      JobId: string;
      EntityGroupID: string;
      EntityID: string;
      ProjectId: string;
    };
    [key: string]: unknown;
  };
}

// ── Constants ──────────────────────────────────────────────────────────────────

// Legacy `OpenAIQuestionLength` (dev/qa = 1000). Env-driven for per-env parity.
const MAX_QUESTION_LENGTH = envNumber(import.meta.env.VITE_OPENAI_QUESTION_LENGTH, 1000);

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatTime(date?: Date): string {
  return (date ?? new Date()).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
}

function getUserInitials(): string {
  try {
    const user = authModule.user;
    if (!user?.name) return '';
    const parts = user.name.split(' ');
    return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
  } catch {
    return '';
  }
}

function avatarType(): 'ey' | 'client' {
  return getCurrentUserType() === 'internal' ? 'ey' : 'client';
}

/**
 * Sanitize HTML response from the chatbot API.
 * Mirrors Angular's DomSanitizer — strips scripts/event handlers but keeps
 * safe HTML (tables, links, formatting). Uses the browser's native DOMParser
 * with a tag/attribute whitelist (no external dependency needed).
 *
 * The `style` attribute is allowed to match Angular's `bypassSecurityTrustHtml`
 * behavior for rich responses (income tables, MFE navigation links).
 */
const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'i',
  'em',
  'strong',
  'p',
  'br',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'span',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'pre',
  'code',
]);

const ALLOWED_ATTRS = new Set(['class', 'href', 'target', 'data-url', 'rel', 'style']);

function sanitizeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? '';
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node as Element;
  const tag = el.tagName.toLowerCase();

  if (!ALLOWED_TAGS.has(tag)) {
    // Recursively render children but drop the disallowed tag itself
    let inner = '';
    el.childNodes.forEach((child) => {
      inner += sanitizeNode(child);
    });
    return inner;
  }

  // Build safe attributes
  let attrs = '';
  for (const attr of Array.from(el.attributes)) {
    if (ALLOWED_ATTRS.has(attr.name.toLowerCase())) {
      // Block javascript: URIs in href
      if (attr.name === 'href' && /^\s*javascript:/i.test(attr.value)) continue;
      attrs += ` ${attr.name}="${attr.value.replace(/"/g, '&quot;')}"`;
    }
  }

  // Self-closing tags
  if (tag === 'br') return `<br${attrs} />`;

  let inner = '';
  el.childNodes.forEach((child) => {
    inner += sanitizeNode(child);
  });
  return `<${tag}${attrs}>${inner}</${tag}>`;
}

function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let result = '';
  doc.body.childNodes.forEach((node) => {
    result += sanitizeNode(node);
  });
  return result;
}

/**
 * Format currency matching Angular's `formatCurrency` in the chatbot component.
 * Negative values shown in parentheses without the `-` sign.
 */
function formatCurrency(value: number): string {
  if (value < 0) {
    const formatted = Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(value)
      .replace('-', '');
    return `(${formatted})`;
  }
  return Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Build income summary table HTML from structured data.
 * Mirrors Angular's Renderer2 table construction in `getChatMessageFromSemanticKernel`.
 */
function buildIncomeSummaryTable(items: { scheduleLine: string; taxTotal: number }[]): string {
  const escapeHtml = (text: string) =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const rows = items
    .map(
      (item) =>
        `<tr><td class="chatbot-custom-td">${escapeHtml(item.scheduleLine)}</td>` +
        `<td class="chatbot-custom-td">${formatCurrency(item.taxTotal).replace('$', '')}</td></tr>`,
    )
    .join('');
  return (
    `<table class="chatbot-custom-table">` +
    `<tr><th class="chatbot-custom-th">Line Item</th><th class="chatbot-custom-th">Total Tax</th></tr>` +
    `${rows}</table>`
  );
}

/**
 * Handle PDF attachment download from base64 response.
 * Mirrors Angular's attachment download logic in `getChatMessageFromSemanticKernel`.
 */
function downloadPdfAttachment(base64Data: string): void {
  try {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([byteNumbers], { type: 'application/pdf' });
    const date = new Date();
    const fileName = `Sample_K1_${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}.pdf`;

    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = fileName;
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    // Prevent the programmatic click from bubbling to the document-level
    // click-outside handler, which would otherwise close the chatbot panel.
    downloadLink.addEventListener('click', (e) => e.stopPropagation());
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(downloadLink.href);
  } catch (error) {
    console.error('Failed to download the file', error);
  }
}

/**
 * Handle print job data from chatbot response.
 * Mirrors Angular's `addNewPrintJob` localStorage setup.
 */
function handlePrintJobData(
  printJobData: { JobId: string; EntityGroupID: string; EntityID: string; ProjectId: string },
  clientId: string,
): void {
  localStorage.setItem('X-ClientID', clientId);
  localStorage.setItem('X-JobId', printJobData.JobId.toLowerCase());
  localStorage.setItem('X-ProjectId', printJobData.ProjectId.toLowerCase());
  localStorage.setItem('X-EntityGroupID', printJobData.EntityGroupID.toLowerCase());
  localStorage.setItem('X-EntityID', printJobData.EntityID.toLowerCase());
  localStorage.setItem('RedirecttoPrint', 'true');
}

/**
 * Check if a follow-up question is an HTML link (contains `<a` with `data-url`).
 * Mirrors Angular's template check: `question.includes('<a') && question.includes('data-url')`.
 */
function isHtmlLinkQuestion(question: string): boolean {
  return question.includes('<a') && question.includes('data-url');
}

// ── Component ──────────────────────────────────────────────────────────────────

export function ChatbotPanel({ onClose }: { onClose: () => void }) {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [currentTimeNow, setCurrentTimeNow] = useState(formatTime());
  const scrollRef = useRef<HTMLDivElement>(null);
  const clientId = useClientStore((s) => s.current?.id);
  const userEmail = authModule.user?.email;

  // Ref to track whether this is the initial mount (skip clearMessages on first render)
  const isInitialMount = useRef(true);
  // Ref to track previous clientId for change detection
  const prevClientIdRef = useRef(clientId);

  // Scroll behavior refs (mirrors Angular's isNearBottom / autoScrollEnabled)
  const isNearBottomRef = useRef(true);
  const isMouseWheelRef = useRef(false);
  const autoScrollEnabledRef = useRef(true);

  // Zustand store — persists across panel open/close (mirrors HeaderChatbotService)
  const messages = useChatbotStore((s) => s.messages);
  const appendMessage = useChatbotStore((s) => s.appendMessage);
  const clearMessages = useChatbotStore((s) => s.clearMessages);
  const revealLastMessage = useChatbotStore((s) => s.revealLastMessage);
  const removeStatusMessages = useChatbotStore((s) => s.removeStatusMessages);
  const updateMessage = useChatbotStore((s) => s.updateMessage);

  // User avatar (mirrors Angular's getUserProfilePicture → getRumUserAvatarByUserEmail)
  const { data: avatarSrc } = useUserAvatar(userEmail);

  // Update clock every second (mirrors Angular's setInterval → updateTime)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimeNow(formatTime());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Clear history on client change ONLY (not on initial mount).
  // Mirrors Angular's EventTypes.CLIENT.CHANGE event handler which only fires
  // when an actual client switch event is emitted, not on component init.
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Only clear if clientId actually changed (not just a remount)
    if (prevClientIdRef.current !== clientId) {
      prevClientIdRef.current = clientId;
      clearMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Auto-scroll to bottom when messages change — but only if user hasn't scrolled up.
  // Mirrors Angular's isNearBottom / autoScrollEnabled logic.
  useEffect(() => {
    if (isNearBottomRef.current && autoScrollEnabledRef.current) {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  /**
   * Wheel handler — mirrors Angular's `@HostListener('wheel')`.
   * Disables auto-scroll when user manually scrolls.
   */
  const handleWheel = useCallback(() => {
    // The message list has `overflow-y: auto`, so the browser scrolls it natively
    // on wheel. We only record that the user scrolled manually (to disable
    // auto-scroll); we must NOT also move scrollTop here or the list scrolls twice.
    isMouseWheelRef.current = true;
    isNearBottomRef.current = false;
  }, []);

  /**
   * Scroll handler — mirrors Angular's `@HostListener('scroll')`.
   * Detects when user has scrolled away from bottom.
   */
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (isMouseWheelRef.current) {
      isMouseWheelRef.current = false;
      return;
    }
    const element = e.target as HTMLElement;
    const atBottom = Math.ceil(element.scrollTop + element.clientHeight) >= element.scrollHeight;
    if (!atBottom) {
      autoScrollEnabledRef.current = false;
      isNearBottomRef.current = false;
    }
  }, []);

  /**
   * Route-to-MFE click handler — mirrors Angular's `handleClick($event)`.
   * Intercepts clicks on `.routetomfeinCB` anchor tags and routes via
   * the shell router instead of navigating away.
   */
  const handleBubbleClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'A' && target.classList.contains('routetomfeinCB')) {
      e.preventDefault();
      const url = target.getAttribute('data-url');
      if (url) {
        // Angular: if not on home, route to home first then delay 2s
        if (!window.location.href.includes('/home')) {
          navigateBrowserToLegacyTarget('home');
          setTimeout(() => navigateBrowserToLegacyTarget(url), 2000);
        } else {
          navigateBrowserToLegacyTarget(url);
        }
      }
    }
  }, []);

  /**
   * Handle click on an HTML link follow-up question pill.
   * Mirrors Angular's `(click)="handleClick($event)"` on the link-type pill.
   */
  const handleFollowUpLinkClick = useCallback((e: React.MouseEvent, htmlContent: string) => {
    // Parse the HTML to extract the data-url
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const anchor = tempDiv.querySelector('a[data-url]');
    if (anchor) {
      e.preventDefault();
      const url = anchor.getAttribute('data-url');
      if (url) {
        if (!window.location.href.includes('/home')) {
          navigateBrowserToLegacyTarget('home');
          setTimeout(() => navigateBrowserToLegacyTarget(url), 2000);
        } else {
          navigateBrowserToLegacyTarget(url);
        }
      }
    }
  }, []);

  /**
   * Extract display text from an HTML follow-up question.
   * Strips HTML tags to get the plain text label for the pill.
   */
  const getFollowUpLabel = useCallback((question: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = question;
    return tempDiv.textContent ?? question;
  }, []);

  const sendMessage = async (questionText?: string) => {
    const text = (questionText ?? input).trim();
    if (!text || isLoading) return;
    if (!questionText) setInput('');

    // Re-enable auto-scroll when user sends a message (mirrors Angular)
    isNearBottomRef.current = true;
    autoScrollEnabledRef.current = true;

    // User message — capture timestamp at creation
    appendMessage({
      role: 'user',
      content: text,
      typing: false,
      isLiked: false,
      isDisLiked: false,
      timestamp: formatTime(),
    });
    setIsLoading(true);

    // Length validation
    if (text.length > MAX_QUESTION_LENGTH) {
      appendMessage({
        role: 'assistant',
        content: `Please restrict the question to ${MAX_QUESTION_LENGTH} characters.`,
        typing: true,
        isLiked: false,
        isDisLiked: false,
        timestamp: formatTime(),
      });
      setTimeout(() => revealLastMessage(), 2000);
      setIsLoading(false);
      return;
    }

    // Build chat history for context-based chat (mirrors Angular enableContextBasedChat)
    const chatHistory = messages
      .filter((m) => !m.isStatusMessage)
      .map(({ role, content }) => ({ role, content: String(content) }));
    chatHistory.push({ role: 'user', content: text });

    // Status timeout — show "processing" after 2.5s (mirrors Angular)
    let statusAdded = false;
    const statusTimer = setTimeout(() => {
      statusAdded = true;
      appendMessage({
        role: 'assistant',
        content: 'Processing your request, please wait...',
        typing: false,
        isLiked: false,
        isDisLiked: false,
        isStatusMessage: true,
        timestamp: formatTime(),
      });
    }, 2500);

    try {
      const result = await http<ChatResponse>(Endpoints.chatbotAnswer(), {
        method: 'POST',
        json: { chatHistory },
      });

      clearTimeout(statusTimer);
      if (statusAdded) removeStatusMessages();

      if (!result) {
        appendMessage({
          role: 'assistant',
          content: "Oops. I can't understand!",
          typing: true,
          isLiked: false,
          isDisLiked: false,
          timestamp: formatTime(),
        });
      } else {
        let responseContent = result.response;

        // Handle structured data — income summary table (mirrors Angular)
        if (result.data?.incomeSummary?.items) {
          responseContent += '\n\n' + buildIncomeSummaryTable(result.data.incomeSummary.items);
        }

        // Handle print job data — set localStorage (mirrors Angular)
        if (result.data?.addNewPrintJob && clientId) {
          handlePrintJobData(result.data.addNewPrintJob, clientId);
        }

        // Sanitize HTML response (mirrors Angular DomSanitizer)
        const sanitized = sanitizeHtml(responseContent);

        appendMessage({
          role: 'assistant',
          content: sanitized,
          questionId: result.questionId,
          followUpQuestions: result.followQuestions,
          typing: true,
          isLiked: false,
          isDisLiked: false,
          timestamp: formatTime(),
        });

        // Handle PDF attachment download (mirrors Angular)
        if (result.attachments) {
          downloadPdfAttachment(result.attachments);
        }
      }
      setTimeout(() => revealLastMessage(), 2000);
    } catch (err: unknown) {
      clearTimeout(statusTimer);
      if (statusAdded) removeStatusMessages();
      const status = (err as { status?: number })?.status;
      let errorMsg = 'Something went wrong. Please try again.';
      if (status === 401) errorMsg = 'Unauthorized Access. Please reach out to administrator.';
      if (status === 400) errorMsg = 'Invalid input.';
      appendMessage({
        role: 'assistant',
        content: errorMsg,
        typing: true,
        isLiked: false,
        isDisLiked: false,
        timestamp: formatTime(),
      });
      setTimeout(() => revealLastMessage(), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Like/dislike handler — mirrors Angular's `likedMesage` / `dislikedMessage`.
   * Toggles the state and fires the PUT to update the log.
   */
  const handleLike = async (index: number, liked: boolean) => {
    const msg = messages[index];
    if (!msg) return;

    if (liked) {
      const newIsLiked = !msg.isLiked;
      updateMessage(index, { isLiked: newIsLiked, isDisLiked: false });
      if (msg.questionId) {
        try {
          await http(Endpoints.chatbotFeedback(), {
            method: 'PUT',
            json: { questionId: msg.questionId, isLiked: newIsLiked, isDisLiked: false },
          });
        } catch {
          /* best-effort */
        }
      }
    } else {
      const newIsDisLiked = !msg.isDisLiked;
      updateMessage(index, { isDisLiked: newIsDisLiked, isLiked: false });
      if (msg.questionId) {
        try {
          await http(Endpoints.chatbotFeedback(), {
            method: 'PUT',
            json: { questionId: msg.questionId, isLiked: false, isDisLiked: newIsDisLiked },
          });
        } catch {
          /* best-effort */
        }
      }
    }
  };

  /**
   * Copy message content to clipboard — mirrors Angular's `copyToggle` + `cdkCopyToClipboard`.
   * Shows a checkmark icon for 3 seconds after copying.
   */
  const handleCopy = (index: number, content: string) => {
    // Strip HTML tags for clipboard (mirrors Angular sanitizeContent)
    const textContent = content.replace(/<[^>]*>/g, '');
    navigator.clipboard.writeText(textContent).catch(() => {
      /* best-effort */
    });
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 3000);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    getTracker().event('Chatbot-message-sent');
    void sendMessage();
  };

  const handleClearHistory = () => {
    clearMessages();
  };

  const initials = getUserInitials();
  const isEyUser = avatarType() === 'ey';

  return (
    <div className="chatbot-panel">
      {/* Header */}
      <div className="chatbot-panel__header">
        <h3>EYQ FS</h3>
        <div className="chatbot-panel__header-actions">
          <button
            type="button"
            className="chatbot-panel__header-btn"
            aria-label="Clear chat history"
            title="Clear chat history"
            onClick={handleClearHistory}
          >
            <MotifIcon
              src={IconoirClockRotateRight}
              iconAriaLabel="Clear history"
              fill="#2e2e38"
              size={18}
            />
          </button>
          <button
            type="button"
            className="chatbot-panel__header-btn"
            aria-label="Close chatbot"
            title="Close chatbot"
            onClick={onClose}
          >
            <MotifIcon src={IconoirXmark} iconAriaLabel="Close" fill="#2e2e38" size={18} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        className="chatbot-panel__messages"
        ref={scrollRef}
        onWheel={handleWheel}
        onScroll={handleScroll}
      >
        {/* Welcome */}
        <div className="chatbot-panel__msg-row chatbot-panel__msg-row--bot">
          <div className="chatbot-panel__avatar chatbot-panel__avatar--bot">
            <img src="/Avatar_YouTube.png" alt="Chat bot" className="chatbot-panel__avatar-img" />
          </div>
          <div className="chatbot-panel__bubble chatbot-panel__bubble--bot">
            Hello, what can I help you with today?
            <span className="chatbot-panel__time">{currentTimeNow}</span>
          </div>
        </div>

        {messages.map((msg, i) => {
          if (msg.isStatusMessage && msg.role === 'assistant') {
            return (
              <div
                key={`status-${i}`}
                className="chatbot-panel__msg-row chatbot-panel__msg-row--bot"
                aria-live="polite"
                aria-atomic="true"
              >
                <div className="chatbot-panel__avatar chatbot-panel__avatar--bot">
                  <img
                    src="/Avatar_YouTube.png"
                    alt="Chat bot"
                    className="chatbot-panel__avatar-img"
                  />
                </div>
                <div className="chatbot-panel__bubble chatbot-panel__bubble--bot chatbot-panel__bubble--status">
                  {msg.content}
                </div>
              </div>
            );
          }

          if (msg.role === 'user') {
            return (
              <div
                key={i}
                className="chatbot-panel__msg-row chatbot-panel__msg-row--user"
                aria-live="polite"
                aria-atomic="true"
              >
                <div className="chatbot-panel__bubble chatbot-panel__bubble--user">
                  {msg.content}
                  <span className="chatbot-panel__time">{msg.timestamp ?? currentTimeNow}</span>
                </div>
                <div
                  className={`chatbot-panel__avatar chatbot-panel__avatar--user ${isEyUser ? 'chatbot-panel__avatar--ey' : ''}`}
                >
                  {avatarSrc && isEyUser ? (
                    <img src={avatarSrc} alt={initials} className="chatbot-panel__avatar-img" />
                  ) : (
                    <span>{initials}</span>
                  )}
                </div>
              </div>
            );
          }

          // Assistant message
          return (
            <div
              key={i}
              className="chatbot-panel__msg-row chatbot-panel__msg-row--bot"
              aria-live="polite"
              aria-atomic="true"
            >
              <div className="chatbot-panel__avatar chatbot-panel__avatar--bot">
                <img
                  src="/Avatar_YouTube.png"
                  alt="Chat bot"
                  className="chatbot-panel__avatar-img"
                />
              </div>
              {msg.typing ? (
                <div className="chatbot-panel__typing">
                  <span className="chatbot-panel__dot" />
                  <span className="chatbot-panel__dot" />
                  <span className="chatbot-panel__dot" />
                </div>
              ) : (
                <div className="chatbot-panel__bubble chatbot-panel__bubble--bot">
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                  <div
                    dangerouslySetInnerHTML={{ __html: msg.content }}
                    onClick={handleBubbleClick}
                  />
                  {/* Follow-up questions — mirrors Angular ey-pills */}
                  {msg.followUpQuestions && msg.followUpQuestions.length > 0 && (
                    <div className="chatbot-panel__followups">
                      {msg.followUpQuestions.map((q, qi) =>
                        isHtmlLinkQuestion(q) ? (
                          // HTML link follow-up — navigate to MFE route on click
                          <MotifButton
                            key={qi}
                            variant="ghost"
                            size="sm"
                            className="chatbot-panel__pill"
                            disabled={isLoading}
                            onClick={(e: React.MouseEvent) => handleFollowUpLinkClick(e, q)}
                          >
                            {getFollowUpLabel(q)}
                          </MotifButton>
                        ) : (
                          // Normal text follow-up — send as new question
                          <MotifButton
                            key={qi}
                            variant="ghost"
                            size="sm"
                            className="chatbot-panel__pill"
                            disabled={isLoading}
                            onClick={() => void sendMessage(q)}
                          >
                            {q}
                          </MotifButton>
                        ),
                      )}
                    </div>
                  )}
                  {/* Like / Dislike / Copy — mirrors Angular actions row */}
                  {!msg.isStatusMessage && (
                    <div className="chatbot-panel__feedback">
                      <MotifButton
                        variant="text"
                        size="xs"
                        shape="icon"
                        active={msg.isLiked}
                        buttonAriaLabel="Like"
                        onClick={() => void handleLike(i, true)}
                      >
                        <MotifIcon
                          src={IconoirThumbsUp}
                          iconAriaLabel="Like"
                          fill={
                            msg.isLiked ? 'var(--motif-color-brand-primary, #ffe600)' : '#ffffff'
                          }
                          size={16}
                        />
                      </MotifButton>
                      <MotifButton
                        variant="text"
                        size="xs"
                        shape="icon"
                        active={msg.isDisLiked}
                        buttonAriaLabel="Dislike"
                        onClick={() => void handleLike(i, false)}
                      >
                        <MotifIcon
                          src={IconoirThumbsDown}
                          iconAriaLabel="Dislike"
                          fill={
                            msg.isDisLiked ? 'var(--motif-color-brand-primary, #ffe600)' : '#ffffff'
                          }
                          size={16}
                        />
                      </MotifButton>
                      <MotifButton
                        variant="text"
                        size="xs"
                        shape="icon"
                        buttonAriaLabel="Copy"
                        onClick={() => handleCopy(i, msg.content)}
                      >
                        <MotifIcon
                          src={copiedIndex === i ? IconoirCheck : IconoirCopy}
                          iconAriaLabel="Copy"
                          fill={
                            copiedIndex === i
                              ? 'var(--motif-color-brand-primary, #ffe600)'
                              : '#ffffff'
                          }
                          size={16}
                        />
                      </MotifButton>
                      <span className="chatbot-panel__time">{msg.timestamp ?? currentTimeNow}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer disclaimer (mirrors Angular chatbotFooterCustomContent) */}
      <div className="chatbot-panel__disclaimer">
        Responses are AI generated and should be verified.
      </div>

      {/* Input */}
      <form className="chatbot-panel__input-area" onSubmit={handleSubmit}>
        <input
          className="chatbot-panel__input"
          type="text"
          placeholder="Start conversation with ChatBot..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={isLoading}
          aria-label="Chat message"
        />
        <button
          type="submit"
          className="chatbot-panel__send-btn"
          disabled={isLoading || !input.trim()}
          aria-label="Send message"
        >
          <MotifIcon src={IconoirSendDiagonal} iconAriaLabel="Send" fill="#ffe600" size={20} />
        </button>
      </form>
    </div>
  );
}
